import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { request } from '../helpers/request';

const RefundRequestModal = ({ isOpen, onClose, orderId, product }) => {
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await request(SummaryApi.requestRefund, {
                body: JSON.stringify({
                    orderId,
                    productId: product.productId,
                    reason,
                    description,
                    images: [] // Image upload logic can be added later
                })
            });

            const resData = await response.json();
            if (resData.success) {
                toast.success(resData.message);
                onClose();
            } else {
                toast.error(resData.message);
            }
        } catch (err) {
            toast.error("Failed to submit refund request");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <div className='fixed inset-0 z-[100] flex items-center justify-center px-4'>
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className='absolute inset-0 bg-primary/60 backdrop-blur-sm'
                />
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    className='relative bg-white w-full max-w-lg rounded-[2.5rem] p-8 md:p-10 shadow-2xl'
                >
                    <button onClick={onClose} className='absolute top-6 right-6 p-2 hover:bg-surface-muted rounded-full transition-all'>
                        <FaTimes className='text-gray-400' />
                    </button>

                    <h2 className='text-2xl font-black text-primary mb-2'>Return Request</h2>
                    <p className='text-sm text-gray-500 mb-8 font-medium'>Tell us what's wrong with the {product.name}</p>

                    <form onSubmit={handleSubmit} className='space-y-6'>
                        <div>
                            <label className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block'>Reason for Return</label>
                            <select 
                                required
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className='w-full bg-surface-muted rounded-2xl p-4 outline-none border border-transparent focus:border-primary transition-all font-bold text-sm appearance-none'
                            >
                                <option value="">Select a reason</option>
                                <option value="Wrong item received">Wrong item received</option>
                                <option value="Damaged or defective">Damaged or defective</option>
                                <option value="Does not fit">Does not fit</option>
                                <option value="No longer needed">No longer needed</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block'>Detailed Description</label>
                            <textarea 
                                required
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Explain the issue in detail..."
                                className='w-full bg-surface-muted rounded-2xl p-4 h-32 outline-none border border-transparent focus:border-primary transition-all font-medium text-sm resize-none'
                            />
                        </div>

                        <button 
                            type="submit"
                            disabled={loading}
                            className='w-full py-4 bg-primary text-white font-black rounded-full hover:bg-accent transition-all shadow-xl shadow-primary/20 active:scale-95 disabled:opacity-50'
                        >
                            {loading ? "Submitting..." : "Submit Return Request"}
                        </button>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default RefundRequestModal;
